// app/[locale]/(routes)/crm/components/OpportunitiesView.tsx
"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { columns } from "../opportunities/table-components/columns";
import { OpportunitiesDataTable } from "../opportunities/table-components/data-table";
import { craftColumns } from "../opportunities/table-components/craft-columns";
import { CraftDataTable } from "../opportunities/table-components/craft-data-table";

interface OpportunityData {
  id: string;
  name: string;
  description: string;
  next_step: string | null;
  status: string | null;
  budget: number | null;
  expected_revenue: number | null;
  assigned_leadsIDs: string | null;
  account: string | null;
  created_on: string | null;
  sales_stage: string | null;
  sales_stage2: string | null;
  sales_stage3: string | null;
  assigned_account: {
    name: string;
  } | null;
  assigned_sales_stage: {
    name: string;
  } | null;
  assigned_to_user: {
    name: string | null;
  } | null;
  assigned_leads: {
    id: string;
    status: string | null;
    firstName?: string | null;
    lastName?: string | null;
    city?: string | null;
    state?: string | null;
    advisorName?: string | null;
    advisorEmail?: string | null;
  } | null;
}

interface OpportunitiesViewProps {
  data: any[]; // Use any[] for input data since it comes from Prisma
  leadId?: string;
  crmData: any;
  accountId?: string;
  userRole?: string; // Add userRole prop
}

const OpportunitiesView = ({
  data,
  leadId,
  crmData,
  accountId,
  userRole,
}: OpportunitiesViewProps) => {
  const router = useRouter();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  // Filter out opportunities where the associated lead is hidden
  let visibleOpportunities = data ? data.filter((opportunity) => {
    if (!opportunity.assigned_leads) {
      return true;
    }
    return opportunity.assigned_leads.status !== "HIDDEN";
  }) : [];

  // Apply role-based filtering
  if (userRole === "craft") {
    // Filter to only show Craft Capital opportunities
    visibleOpportunities = visibleOpportunities.filter((opportunity) => {
      // Check if assigned_account name matches "Craft Capital"
      if (opportunity.assigned_account?.name) {
        return opportunity.assigned_account.name.trim().toLowerCase() === "craft capital".toLowerCase();
      }
      // If no assigned_account, check the account field for Craft Capital ID
      if (opportunity.account === "clu7nocab00087p77ul59mab4") {
        return true;
      }
      return false;
    });
  } else if (userRole === "bevel") {
    // Filter to only show Bevel opportunities
    visibleOpportunities = visibleOpportunities.filter((opportunity) => {
      // Check if assigned_account name matches "Bevel"
      if (opportunity.assigned_account?.name) {
        return opportunity.assigned_account.name.trim().toLowerCase() === "bevel".toLowerCase();
      }
      // If no assigned_account, check the account field for Bevel ID
      if (opportunity.account === "clu7nn3in00067p77cwuq0qbd") {
        return true;
      }
      return false;
    });
  }
  // If role is admin or any other role, show all opportunities (no additional filtering)

  // Transform the data to match the expected format
  const formattedOpportunities = visibleOpportunities.map(opp => ({
    ...opp,
    name: opp.name || "", // Ensure name is never null
    description: opp.description || "", // Ensure description is never null
    created_on: opp.created_on ? new Date(opp.created_on).toISOString() : null,
  }));

  // Dynamic title based on role
  const getTitle = () => {
    if (userRole === "craft") return "Craft Capital Deals";
    if (userRole === "bevel") return "Bevel Deals";
    return "My Deals";
  };

  // For craft role, use the new CraftDataTable
  if (userRole === "craft") {
    return (
      <Card className="shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex justify-between">
            <div>
              <CardTitle>
                {getTitle()} 
              </CardTitle>
              <CardDescription>
                View and manage your Craft Capital deals
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {!formattedOpportunities || formattedOpportunities.length === 0 ? (
            <div className="py-4 text-center text-gray-500">
              No Craft Capital deals found
            </div>
          ) : (
            <CraftDataTable 
              data={formattedOpportunities} 
              columns={craftColumns}
            />
          )}
        </CardContent>
      </Card>
    );
  }

  // For all other roles, use the regular OpportunitiesDataTable
  return (
    <Card className="shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex justify-between">
          <div>
            <CardTitle>
              {getTitle()} 
            </CardTitle>
            <CardDescription>
              View and manage your active deals
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {!formattedOpportunities || formattedOpportunities.length === 0 ? (
          <div className="py-4 text-center text-gray-500">
            No assigned opportunities found
          </div>
        ) : (
          <OpportunitiesDataTable 
            data={formattedOpportunities} 
            columns={columns}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default OpportunitiesView;